let url;
let isPortal = false;
if (typeof window !== 'undefined' && window) url = window.location.hostname.charAt(0).toUpperCase() + window.location.hostname.slice(1) + '.no';
if (typeof window !== 'undefined' && window && (window.location.href.toLowerCase().includes('fixit.no') || window.location.href.toLowerCase().includes('fixit.test.odindata.no'))) isPortal = true;

/**
 * @param self The vue instance (this).
 * @returns If portal: the users location based on his lat/long coordinates. If salon homepage: The user's lat and long coordinates.
 */
export async function getCurrentLocation(self) {
    const { latitude, longitude, ...response } = Object(await getCurrentPosition().catch(error => {
        if (error) {
            switch (error.code) {
                case error.PERMISSION_DENIED:
                    if (isPortal) {
                        self.$google.sendEventButtonClick('geolocation-select-location', 'frontpage', 'not allowed');  
                        self.$eventBus.$emit('snackbar', {
                            text: `Du har ikke gitt ${url} tillatelse til å bruke din lokasjon. Fjern blokkeringen i innstillingene for nettleseren din og last inn siden på nytt.`,
                            type: 'warning',
                            closeButton: true
                        });
                    } 
                    return { denied: true };
                default:
                    if (isPortal) {
                        self.$google.sendEventButtonClick('geolocation-select-location', 'frontpage', 'error');
                        self.$eventBus.$emit('snackbar', {
                            text: 'Kunne dessverre ikke finne din fysiske plassering. Prøv på nytt.',
                            type: 'warning'
                        });
                    }
                    return { error: true };
            }
        } else {
            if (isPortal) {
                self.$google.sendEventButtonClick('geolocation-select-location', 'frontpage', 'error');  
                self.$eventBus.$emit('snackbar', {
                    text: 'Beklager. Nettleseren din støtter ikke geo-lokalisering. Prøv å søk etter stedet ditt i søkefeltet.',
                    type: 'warning'
                });
            }
            return { error: true };
        }
    }));

    // Successfully fetched current position
    if (latitude && longitude) {
        if (isPortal) {
            self.$google.sendEventButtonClick('geolocation-select-location', 'frontpage', 'success');
            return await self.$store.dispatch('search/getLocationFromLatLng', { latitude, longitude });
        } else {
            return {userLat: latitude, userLong: longitude}
        }
    }

    return response;
}

/**
 * This method calculates each passed salon's distance to the current user's location in km, 
 * and sorts the list in a increasing order (closest salon first). If a chain have several salons with the same address, 
 * then we first sort those adresses by sort number, and then place them all in the same place in the list of distance sorted salons
 * based on proximity to the user. 
 * @param self The vue instance (this).
 * @param {Array} salons An array of salon objects.
 */
export async function sortSalonListByUserLocation(self, salons) {
    let userLocation = await getCurrentLocation(self)
    if (!userLocation.denied && !userLocation.error) {
        //Get distance from user and sort salons by this distance
        salons.forEach(salon => {
            //Attaching returned distance from function to the salon
            Object.assign(salon, { distanceFromUser: getDistanceFromLatLonInKm(userLocation.userLat, userLocation.userLong, salon.mapLatitude, salon.mapLongitude)  })
        })
        salons.sort(function(a, b) { return a.distanceFromUser - b.distanceFromUser });

        //Split the salons into distinct adresses and duplicate adresses
        let duplicatesCount = getDuplicates(salons)
        let distinctSalonAdresses = salons.filter(e => duplicatesCount[e.address] == 0)
        let duplicateSalonAdresses = salons.filter(e => duplicatesCount[e.address] >= 1).reduce((res, curr) =>
        {
            if (res[curr.address]) res[curr.address].push(curr);
            else Object.assign(res, {[curr.address]: [curr]});
            //sort the different duplicate adresses by sort number
            for (const list in res) { 
                res[list] = res[list].sort(function(a, b) { return a.sortNo - b.sortNo }) 
            }
            return res
        }, {});

        // Insert the duplicate adresses salons into the list of distinct adresses salons at the correct index. The 
        // correct index is based on distance from user. So the duplicates are sorted by sortNo, but placed correctly next to eachother in the
        // correct place based on distance.
        let sortedList = distinctSalonAdresses
        for (const list in duplicateSalonAdresses) {
            let placementIndex = sortedList.findIndex(x => x.distanceFromUser > duplicateSalonAdresses[list][0].distanceFromUser)
            sortedList = sortedList.slice(0, placementIndex).concat(duplicateSalonAdresses[list], sortedList.slice(placementIndex));
        }
        return sortedList

    } else salons.sort(function(a, b) {return a.sortNo - b.sortNo})
}

/**
 * 
 * @param {Number} userLat The users latitude value
 * @param {Number} userLong The users longitude value
 * @param {Number} salonLat The salon's latitude value
 * @param {Number} salonLong The salon's longitude value
 * @returns The distance from the user to a salon in kilometers. If you'd like the distance in
 * meters, simply multiply the return value by 1000
 */
function getDistanceFromLatLonInKm(userLat, userLong, salonLat, salonLong) {
    var salonLat = salonLat.includes(',') ? salonLat.replace(',', '.') : salonLat;
    var salonLong = salonLong.includes(',') ? salonLong.replace(',', '.') : salonLong;
    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(salonLat - userLat);  // deg2rad below
    var dLon = deg2rad(salonLong - userLong);
    var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(userLat)) * Math.cos(deg2rad(salonLat)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    var d = R * c; // Distance in km
    return d;  // distance returned
    
    function deg2rad(deg) {
        return deg * (Math.PI / 180)
    }
}

/**
 * This function checks if the user's browser is compatible with geolocation, and promts
 * a dialog where location can be shared.
 * @returns The users lat and long coordinates, or error.
 */
function getCurrentPosition() {
	return new Promise((resolve, reject) => {
        if (!window.navigator.geolocation) reject();

		window.navigator.geolocation.getCurrentPosition(
			// Success
			({ coords }) => {
				resolve(coords);
			},

			// Error
			error => {
				reject(error);
			},

			// Options
			{
				maximumAge: 0,
				timeout: 10000,
				enableHighAccuracy: true
			}
		);
	});
}

function getDuplicates(salons) {
    return salons.reduce((a, e) => { a[e.address] = ++a[e.address] || 0; return a }, {});
}